import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';

import {
  ResultsHeader,
  ResultsView,
} from '../../components/results-header/results-header';
import { ResultsListMap } from '../../components/results-list-map';
import { ResultsTable } from '../../components/tables/results-table';
import { useResultsQueries } from '../../data-access/queries/results';
import { useResultsExport } from '../../hooks/use-results-export';
import { useResultsStats } from '../../hooks/use-results-stats';
import { ResultsClause } from '../../components/results-help/results-clause-help-content';
import { buildResultsListFilters } from '../../lib/results-filters';

import { FullMainHeight } from '@ui';

import { useFiltersStore } from '@data-access';

export const ResultsPage: FC = () => {
  const [view, setView] = useState<ResultsView>('table');

  const resultsQueries = useResultsQueries();
  const filters = useFiltersStore.use['results']().filters;
  const sorting = useFiltersStore.use['results']().sorting;
  const pageIndex = useFiltersStore.use['results']().pagination.pageIndex;

  const clause: ResultsClause =
    filters.find((f) => f.id === 'clause')?.value === 'gp' ? 'gp' : 'new';

  const listFilters = useMemo(
    () => buildResultsListFilters(pageIndex + 1, sorting, filters),
    [pageIndex, sorting, filters],
  );

  const valuesQuery = useQuery(resultsQueries.values());

  const resultsQuery = useQuery({
    ...resultsQueries.list(listFilters),
    enabled: view === 'table',
    placeholderData: keepPreviousData,
  });

  const statsQuery = useResultsStats(filters);

  const {
    handleExport,
    isExportLoading,
    isExportDisabled,
    exportDisabledForClause,
  } = useResultsExport({ clause, filters });

  return (
    <FullMainHeight.Root gap="sm">
      <FullMainHeight.Header>
        <ResultsHeader
          exportDisabled={isExportDisabled}
          exportLoading={isExportLoading}
          exportDisabledForClause={exportDisabledForClause}
          filterValues={valuesQuery.data}
          filterValuesLoading={valuesQuery.isLoading}
          onExport={handleExport}
          onViewChange={setView}
          statsLoading={statsQuery.isLoading}
          summary={statsQuery.data}
          view={view}
        />
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        {view === 'table' ? (
          <ResultsTable
            data={resultsQuery.data?.results ?? []}
            isFetching={resultsQuery.isFetching}
            isPlaceholderData={resultsQuery.isPlaceholderData}
            rowCount={resultsQuery.data?.count ?? 0}
          />
        ) : (
          <ResultsListMap />
        )}
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
};
